// src/helpers/useLanguages.js
import { ref, onMounted, onUnmounted } from 'vue'
import db from '@/firebase/init'
import store from '@/store'

const DEFAULT_LANGS = [
  {
    code: 'en',
    label: 'English',
    isDefault: true,
    order: 1,
    enabled: true,
    rtl: false,
  },
]

export function useLanguages() {
  const enabledLangs = ref([])
  const defaultLang = ref('en') // string code
  let unsub = null

  const docRef = () => db.collection('config').doc('languages')

  const normalize = (arr) => {
    const list = (Array.isArray(arr) ? arr : [])
      .map((e, idx) => ({
        code: String(e.code || '').toLowerCase(),
        label: e.label || (e.code ? e.code.toUpperCase() : ''),
        isDefault: !!e.isDefault,
        order: Number(e.order ?? idx + 1),
        enabled: e.enabled !== false, // default true
        rtl: !!e.rtl,
      }))
      .filter((e) => e.code)

    // ensure exactly one default
    if (!list.some((l) => l.isDefault) && list.length) {
      const en = list.find((l) => l.code === 'en') || list[0]
      list.forEach((l) => {
        l.isDefault = l.code === en.code
      })
    }
    return list
  }

  const apply = (data) => {
    const rawLangs = data?.enabledLangs?.length
      ? data.enabledLangs
      : DEFAULT_LANGS

    const langs = normalize(rawLangs)

    enabledLangs.value = langs
    defaultLang.value = langs.find((l) => l.isDefault)?.code || 'en'
  }

  const reload = async () => {
    try {
      const snap = await docRef().get()
      if (snap.exists) {
        apply(snap.data())
      } else {
        const seeded = await ensureSeed()
        if (seeded) {
          const again = await docRef().get()
          if (again.exists) {
            apply(again.data())
          }
        }
      }
    } catch (error) {
      console.error('useLanguages reload error:', error)
    }
  }

  const save = async () => {
    await docRef().set({ enabledLangs: enabledLangs.value }, { merge: true })
  }

  const addLanguage = async ({
    code,
    label,
    order,
    enabled = true,
    rtl = false,
    isDefault = false,
  }) => {
    code = (code || '').trim().toLowerCase()
    if (!code) return
    if (!enabledLangs.value.some((l) => l.code === code)) {
      let next = [
        ...enabledLangs.value,
        {
          code,
          label: label || code.toUpperCase(),
          order: Number(order ?? (enabledLangs.value.at(-1)?.order || 0) + 1),
          enabled: !!enabled,
          rtl: !!rtl,
          isDefault: !!isDefault,
        },
      ]
      if (isDefault)
        next = next.map((l) => ({ ...l, isDefault: l.code === code }))
      enabledLangs.value = normalize(next)
      await save()

      // Also create a language document in the languages collection
      // This will be used for cleanup when the language is deleted
      try {
        await db
          .collection('languages')
          .doc(code)
          .set({
            code,
            label: label || code.toUpperCase(),
            order: Number(order ?? (enabledLangs.value.at(-1)?.order || 0) + 1),
            enabled: !!enabled,
            rtl: !!rtl,
            isDefault: !!isDefault,
            createdAt: new Date(),
          })
        console.log(`[useLanguages] Created language document: ${code}`)
      } catch (error) {
        console.error(
          `[useLanguages] Error creating language document ${code}:`,
          error
        )
        // Don't throw - the language is already added to enabledLangs
      }
    }
  }

  const updateLanguage = async ({
    code,
    label,
    order,
    enabled,
    rtl,
    isDefault,
  }) => {
    const idx = enabledLangs.value.findIndex((l) => l.code === code)
    if (idx === -1) return
    const cur = enabledLangs.value[idx]
    const next = { ...cur }
    if (label !== undefined) next.label = label
    if (order !== undefined) next.order = Number(order) || 0
    if (enabled !== undefined) next.enabled = !!enabled
    if (rtl !== undefined) next.rtl = !!rtl
    if (isDefault !== undefined) next.isDefault = !!isDefault

    let list = [...enabledLangs.value]
    list[idx] = next
    // re-normalize (enforce single default)
    enabledLangs.value = normalize(list)
    await save()

    // Also update the language document in the languages collection
    try {
      await db.collection('languages').doc(code).set(
        {
          code,
          label: next.label,
          order: next.order,
          enabled: next.enabled,
          rtl: next.rtl,
          isDefault: next.isDefault,
          updatedAt: new Date(),
        },
        { merge: true }
      )
      console.log(`[useLanguages] Updated language document: ${code}`)
    } catch (error) {
      console.error(
        `[useLanguages] Error updating language document ${code}:`,
        error
      )
      // Don't throw - the language is already updated in enabledLangs
    }
  }

  const removeLanguage = async (code) => {
    // Remove from enabledLangs array
    let next = enabledLangs.value.filter((l) => l.code !== code)
    enabledLangs.value = normalize(next)
    await save()

    // Delete the language document from languages collection
    // This will trigger the cleanupDeletedLanguage Cloud Function
    try {
      await db.collection('languages').doc(code).delete()
      console.log(`[useLanguages] Deleted language document: ${code}`)
    } catch (error) {
      console.error(
        `[useLanguages] Error deleting language document ${code}:`,
        error
      )
      // Don't throw - the language is already removed from enabledLangs
    }
  }

  const setDefault = async (code) => {
    enabledLangs.value = normalize(
      enabledLangs.value.map((l) => ({ ...l, isDefault: l.code === code }))
    )
    await save()
  }

  const ensureSeed = async () => {
    try {
      const roles = store.getters['UserData/getRoles'] || []
      const isPrivileged =
        roles.includes('superAdmin') || roles.includes('admin')
      if (!isPrivileged) return false
      await docRef().set({ enabledLangs: DEFAULT_LANGS }, { merge: true })
      return true
    } catch (e) {
      console.warn('[useLanguages] seed failed:', e)
      return false
    }
  }

  onMounted(() => {
    unsub = docRef().onSnapshot(
      (doc) => {
        if (doc.exists) {
          apply(doc.data())
        }
      },
      (error) => {
        console.error('useLanguages onSnapshot error:', error)
      }
    )
    reload()
  })

  onUnmounted(() => unsub && unsub())

  // expose an alias 'languages' for your table
  const languages = enabledLangs

  return {
    languages,
    enabledLangs,
    defaultLang, // string code
    addLanguage,
    updateLanguage,
    removeLanguage,
    setDefault,
    reload,
  }
}
